import { memo, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getValuationChartOptions } from '../config/valuationChartConfig';
import { TIME_HORIZONS, LOOKBACK_WINDOWS, PERIOD_OPTIONS } from '../utils/valuationRegression';
import {
  METRIC_OPTIONS, isYieldMetric, ratioName, yieldName,
} from '../config/metricsConfig';
import DisclaimerMarker from './DisclaimerMarker';
import Spinner from '../../common/Spinner';
import styles from './ValuationSubsequentReturnsCard.module.css';

/**
 * A horizon tab can be disabled/insufficient for two structurally different
 * reasons, and the tooltip must say which one actually applies rather than
 * always blaming "not enough trading history":
 *  (1) the SYMBOL genuinely doesn't have `years` of history yet, or
 *  (2) the current evaluation window is simply too narrow to contain even
 *      one observation with a realized `years`-later price — e.g. a 15Y
 *      evaluation window can never support a 15Y (or longer) return
 *      horizon, no matter how much history the symbol actually has, since
 *      every observation inside the window would need its future price to
 *      land at-or-after "now". This is a self-inflicted, always-true
 *      constraint of the window choice itself, not a data limitation.
 * Distinguishing them (rather than the single generic message) is what
 * actually answers "why is this tab greyed out" instead of implying the
 * symbol lacks history it may well have.
 */
const disabledHorizonReason = (years, lookbackYears) => (
  lookbackYears != null && lookbackYears <= years
    ? `The ${lookbackYears}Y evaluation window can't contain a realized ${years}Y-later return — pick a wider window or a shorter holding period.`
    : `Requires at least ${years} years of trading history`
);

/**
 * Same distinction as `disabledHorizonReason`, phrased for the full-panel
 * placeholder (replacing the chart entirely) rather than a tab tooltip.
 * `years` is `TIME_HORIZONS[horizon] / 12` for the active fixed tab.
 */
const insufficientDataMessage = (horizon, years, lookbackYears, lookbackLabel) => (
  lookbackYears != null && lookbackYears <= years
    ? `The ${lookbackLabel} evaluation window can't contain a realized ${horizon} return — pick a wider window or a shorter holding period.`
    : `Not enough trading history for a reliable ${horizon} backtest yet.`
);

// Tab order/labels + the human-readable "N years" used in the disabled-tab
// tooltip, derived from the same TIME_HORIZONS the calculation module keys
// its results by, rather than re-declaring the horizon list here.
const HORIZON_TABS = Object.entries(TIME_HORIZONS).map(([value, months]) => ({
  value,
  label: value,
  years: months / 12,
}));

// Evaluation-window tabs (10Y…30Y/All), derived from the same LOOKBACK_WINDOWS
// the hook keys its state by rather than re-declaring the list here. Distinct
// from the horizon tabs above: this bounds how far *back* observations are
// drawn from, not the forward holding period.
const LOOKBACK_TABS = Object.keys(LOOKBACK_WINDOWS).map((value) => ({ value, label: value }));

// Canonical valuation label for a correlation-table row: the thing actually
// regressed here is a valuation *multiple* (P/E, P/EBIT, …) or, for a yield
// metric, a *yield* (Earnings Yield / E/EV Yield) — NOT the raw fundamental.
// So the table labels rows with the ratio/yield name (reusing metricsConfig's
// canonical helpers so it can't drift from the chart's own axis labels),
// unlike the metric dropdown above, which intentionally keeps the fundamental
// name ("Earnings", "Sales") since that selector drives the raw-value chart
// too. Keyed by the same `valueBasis` the chart and math use, so a row reads
// "P/E" under Market Cap and "EV/E" under Enterprise Value — never a
// market-cap label over an EV-computed number.
const valuationLabel = (metric, valueBasis) => (
  isYieldMetric(metric) ? yieldName(metric, valueBasis) : ratioName(metric, valueBasis)
);

// Below this |correlation|, a row's fit is treated as essentially FLAT — no
// meaningful directional relationship. Real OLS regressions are almost never
// EXACTLY zero, so without this threshold a pure-noise |r| of, say, 0.02
// would still render a faintly-tilted, colored slope line implying a
// directional signal that isn't statistically there. 0.1 is a conventional
// "negligible correlation" cutoff (|r| < 0.1 explains under ~1% of variance),
// low enough not to suppress any genuine weak-but-real signal. Magnitude is
// orientation-independent, so this check uses the RAW |correlation| and is
// applied before the yield-negation orientation logic — negating can't change
// |r|, so the choice of before/after is immaterial; before is chosen so the
// flat case short-circuits without touching the orientation math at all.
const FLAT_CORRELATION_THRESHOLD = 0.1;

// Max tilt (degrees from horizontal) at |r| = 1; a weaker fit tilts
// proportionally flatter, so the glyph encodes strength AND direction in one
// shape while staying visually calm (a 45° cap reads clearly at this size).
const MAX_TILT_DEG = 45;

/**
 * A tiny inline-SVG slope line whose TILT is the row's regression, read
 * literally off the RAW `correlation` (unflipped, for every metric type):
 * it rises left-to-right when the fitted line on the scatter chart above
 * rises, and falls when that fitted line falls — the same geometry as that
 * chart, full stop, with no per-metric exception. Steeper = stronger fit
 * (angle = signed r × MAX_TILT_DEG); a nearly flat line is a weak
 * relationship regardless of sign. A dash covers the degenerate zero-slope
 * fit and any insufficient-data row.
 *
 * Color carries a SEPARATE economic judgment the literal tilt doesn't: green
 * when the direction is the EXPECTED mean-reversion signal (a richer
 * valuation preceded LOWER returns), red when it's the atypical opposite.
 * "Richer" isn't always what the plotted x-axis measures, so this judgment
 * re-expresses `correlation` as "response to a richer valuation" before
 * reading its sign:
 *  - MULTIPLE metrics (P/E, P/EBIT, …): x = price ÷ metric, higher x IS
 *    richer, so the correlation already points the right way.
 *  - YIELD metrics (dividends — see `isYieldMetric`): x = metric ÷ price,
 *    higher x is CHEAPER, so its correlation is flipped to richer-oriented
 *    for THIS judgment only — the flip never touches the tilt above, so a
 *    yield row's line always visually matches its own chart even though its
 *    color is keyed to the inverted "richer" framing.
 * The tooltip spells the relationship out in full.
 */
const SlopeGlyph = ({ correlation, isInsufficientData, metric }) => {
  // Neutral flat dash for the degenerate exact-zero fit, an insufficient-data
  // row, AND any near-flat relationship whose |correlation| falls below
  // FLAT_CORRELATION_THRESHOLD (raw magnitude — orientation-independent, so
  // checked before the yield negation below). In the flat case we render the
  // dash and apply NO expected/atypical green/red coloring, since there's no
  // meaningful direction to judge. See FLAT_CORRELATION_THRESHOLD's rationale.
  if (isInsufficientData || Math.abs(correlation) < FLAT_CORRELATION_THRESHOLD) {
    return (
      <span
        className={styles.directionFlat}
        title="No meaningful directional relationship (essentially flat)."
      >
        —
      </span>
    );
  }
  // The TILT is the raw, unflipped correlation — literally the same sign the
  // scatter chart above plots on its own (uninverted) x-axis, for every
  // metric type including yields. No richer-orientation flip here; that flip
  // is strictly a coloring concern, applied separately below.
  const clamped = Math.max(-1, Math.min(1, correlation));
  // SVG y grows downward, so a POSITIVE (up-to-the-right) slope needs a
  // NEGATIVE y delta — negate the angle when mapping to screen coordinates.
  const angleRad = (-clamped * MAX_TILT_DEG * Math.PI) / 180;
  const halfW = 18;
  const cx = 22;
  const cy = 11;
  const dx = halfW * Math.cos(angleRad);
  const dy = halfW * Math.sin(angleRad);

  // Re-express the fit as a response to a RICHER valuation (see doc comment
  // above) purely for the expected/atypical color judgment — a positive
  // value here means "richer preceded higher returns", independent of which
  // way the line above happens to visually tilt.
  const richerSigned = isYieldMetric(metric) ? -correlation : correlation;
  // Richer → higher returns is the atypical signal; richer → lower is the
  // expected mean-reversion one. (richerSigned < 0 == expected.)
  const isExpected = richerSigned < 0;
  const strokeClass = isExpected ? styles.slopeExpected : styles.slopeAtypical;
  // The line itself always matches the chart above's own tilt (see doc
  // comment); this sentence is the separate economic (richer-valuation)
  // reading of it, called out explicitly so the color isn't mistaken for a
  // claim about which way the line visually leans.
  const title = isExpected
    ? 'A richer valuation historically preceded LOWER subsequent returns (expected mean-reversion signal). The line tilt matches the chart above; steeper = stronger relationship.'
    : 'A richer valuation historically preceded HIGHER subsequent returns (atypical for this combination). The line tilt matches the chart above; steeper = stronger relationship.';

  return (
    <svg
      className={styles.slopeGlyph}
      width="44"
      height="22"
      viewBox="0 0 44 22"
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <line
        x1={cx - dx}
        y1={cy - dy}
        x2={cx + dx}
        y2={cy + dy}
        className={strokeClass}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
};

/**
 * Renders `useMetricCorrelationTable`'s 32 (metric x fixed horizon) rows in
 * a scrollable table — a static snapshot of an already-sorted array, no
 * interactive re-sorting, so this stays a pure render of its `rows` prop.
 * `rSquared` is the primary, sortable-by magnitude column ("how strong is
 * this fit, regardless of direction"); `CorrelationDirection` supplies the
 * direction `rSquared` alone discards. Insufficient-data rows still render
 * (never silently omitted), but with "N/A" in place of a real number and a
 * dimmed row style, so "not enough history" reads unambiguously differently
 * from "weak fit".
 */
const MetricCorrelationTable = ({ rows, valueBasis }) => (
  <div className={styles.correlationTableSection}>
    <h3 className={styles.correlationTableTitle}>Best Predictors Across Metrics &amp; Timeframes</h3>
    <p className={styles.correlationTableLegend}>
      R&sup2; = fit strength (higher is stronger, regardless of direction).
      {' '}The slope line&apos;s tilt matches that row&apos;s own chart above
      {' '}(steeper = stronger); its color is a separate judgment — green when
      {' '}a richer valuation historically preceded lower returns (the
      {' '}expected mean-reversion signal), red for the atypical opposite. A
      {' '}flat dash (—) means no meaningful directional relationship. N/A =
      {' '}not enough trading history yet.
      <DisclaimerMarker id="ntm-realized" />
    </p>
    <div className={styles.correlationTableScroll}>
      <table className={styles.correlationTable}>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Holding period</th>
            <th>R&sup2;</th>
            <th>Direction</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.metric}-${row.horizon}`}
              className={row.isInsufficientData ? styles.correlationRowInsufficient : undefined}
            >
              <td>{valuationLabel(row.metric, valueBasis)}</td>
              <td>{row.horizon}</td>
              <td>{row.isInsufficientData ? 'N/A' : `${(row.rSquared * 100).toFixed(0)}%`}</td>
              <td>
                <SlopeGlyph
                  correlation={row.correlation}
                  isInsufficientData={row.isInsufficientData}
                  metric={row.metric}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * Dumb card component: visualizes one horizon's valuation-regression result
 * (from `useValuationRegression`) as a Highcharts scatter + fit-line chart,
 * with a [2Y] [5Y] [10Y] [15Y] horizon tab bar, an [10Y]…[30Y] [All]
 * evaluation-window tab bar, and a single-select metric dropdown above it,
 * plus a scrollable table (from `useMetricCorrelationTable`)
 * summarizing fit strength/direction across every metric x fixed horizon at
 * once. All data/derivation lives in the hooks — this component only reads
 * `resultsByHorizon[horizon]`/`correlationRows` and renders. The metric
 * picker is a plain single-select (unlike the ratio chart's multi-select
 * checklist), since the chart above the table only ever backtests one
 * metric at a time (the table itself is not affected by this selection).
 *
 * @param {'2Y'|'5Y'|'10Y'|'15Y'} horizon - the currently-selected tab
 * @param {(horizon: '2Y'|'5Y'|'10Y'|'15Y') => void} onHorizonChange - tab click handler
 * @param {'10Y'|'15Y'|'20Y'|'25Y'|'30Y'|'All'} lookback - the currently-selected
 *   evaluation window (how far back observations are drawn from — orthogonal
 *   to `horizon`, the forward holding period)
 * @param {(lookback: '10Y'|'15Y'|'20Y'|'25Y'|'30Y'|'All') => void} onLookbackChange -
 *   evaluation-window tab click handler
 * @param {({ '2Y': object, '5Y': object, '10Y': object, '15Y': object }|null)} resultsByHorizon -
 *   `useValuationRegression`'s per-horizon results, or `null` while the raw
 *   price/EPS series aren't available yet (renders a loading placeholder).
 *   A horizon a symbol can't support is flagged `isInsufficientData` so its
 *   tab renders disabled.
 * @param {'eps'|'revenue'|'ebitda'|'ebit'|'netIncome'|'fcf'|'ocf'|'dividends'} metric -
 *   the currently-selected comparison metric for this card's own backtest
 * @param {(event: object) => void} onMetricChange - metric `<select>` change handler
 * @param {'ntm'|'ttm'} period - the currently-selected multiple vintage for
 *   this card's own backtest (see valuationRegression.js's module docstring
 *   `period` section) — orthogonal to `metric`, and (like `valueBasis`)
 *   drives BOTH the chart above and the correlation table's ranking, which
 *   recomputes on this basis in lockstep with the chart
 * @param {(event: object) => void} onPeriodChange - period `<select>` change handler
 * @param {'marketCap'|'enterpriseValue'} valueBasis - the currently-selected
 *   company value basis, driving both this card's multiple label (P/E vs
 *   EV/E) and its numerator math off a single value. This card does NOT own
 *   a valueBasis selector of its own — it follows the page's top-of-page
 *   toggle (see Analysis.js), matching the user's mental model that the
 *   toggle at the top drives the whole page. There is deliberately no
 *   displayScale or axisScale prop: a multiple is scale-invariant (Per Share
 *   / Total doesn't change these numbers or labels), and Linear/Log is the
 *   main price chart's y-axis concern, meaningless for this card's
 *   numeric-multiple / %-return axes.
 * @param {Array<Object>|null} correlationRows - `useMetricCorrelationTable`'s
 *   sorted (metric x fixed horizon) rows, or `null` while loading — the
 *   table section is omitted entirely while `null`, matching the loading
 *   gate used above it for the chart.
 */
const ValuationSubsequentReturnsCard = ({
  horizon, onHorizonChange, lookback, onLookbackChange,
  resultsByHorizon, metric, onMetricChange, period, onPeriodChange, valueBasis, correlationRows,
  embedded = false,
}) => {
  const activeResult = resultsByHorizon?.[horizon];
  const lookbackYears = LOOKBACK_WINDOWS[lookback] ?? null;

  // Hooks must run unconditionally on every render (loading or not), so the
  // loading early-return happens below, after this. `activeResult.period`
  // (echoed back by computeValuationRegression) already matches `period`
  // here, but spreading `metric` in explicitly since the result itself
  // carries no metric.
  // `activeResult.valueBasis` (echoed back by computeValuationRegression)
  // already matches the `valueBasis` prop here, but spreading it in
  // explicitly alongside `metric` — the result carries no metric, and being
  // explicit keeps the label source unambiguously tied to the same prop that
  // drove the math.
  const chartOptions = useMemo(
    () => (activeResult ? getValuationChartOptions({ ...activeResult, metric, valueBasis }) : null),
    [activeResult, metric, valueBasis]
  );

  // When `embedded`, a CollapsibleCard owns the outer card chrome + title, so
  // skip the <section> and <h2> and left-align the controls row.
  const inner = (
    <>
      <div className={`${styles.header}${embedded ? ` ${styles.headerEmbedded}` : ''}`}>
        {!embedded && <h2 className={styles.title}>Valuation vs. Subsequent Returns</h2>}
        <div className={styles.pickerField}>
          <span className={styles.pickerLabel} id="valuation-metric-label">Metric</span>
          <select
            className={styles.metricDropdown}
            value={metric}
            onChange={onMetricChange}
            aria-labelledby="valuation-metric-label"
          >
            {METRIC_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className={styles.pickerField}>
          <span className={styles.pickerLabel} id="valuation-period-label">Period</span>
          <select
            className={styles.periodDropdown}
            value={period}
            onChange={onPeriodChange}
            aria-labelledby="valuation-period-label"
            title="NTM: forward multiple (historical points realized, current point analyst-estimated). TTM: trailing multiple (fully realized, including the current point)."
          >
            {PERIOD_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className={styles.pickerField}>
          <span className={styles.pickerLabel} id="valuation-horizon-label">Holding period</span>
          <div className={styles.tabGroup} role="group" aria-labelledby="valuation-horizon-label">
            {HORIZON_TABS.map(({ value, label, years }) => {
              const disabled = Boolean(resultsByHorizon?.[value]?.isInsufficientData);
              return (
                <button
                  key={value}
                  type="button"
                  className={`${styles.tabButton} ${horizon === value ? styles.tabButtonActive : ''}`}
                  aria-pressed={horizon === value}
                  disabled={disabled}
                  title={disabled ? disabledHorizonReason(years, lookbackYears) : undefined}
                  onClick={() => onHorizonChange(value)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.pickerField}>
          <span className={styles.pickerLabel} id="valuation-window-label">Evaluation window</span>
          <div className={styles.tabGroup} role="group" aria-labelledby="valuation-window-label">
            {LOOKBACK_TABS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={`${styles.tabButton} ${lookback === value ? styles.tabButtonActive : ''}`}
                aria-pressed={lookback === value}
                title={value === 'All'
                  ? 'Evaluate over all available history'
                  : `Limit the evaluation to the past ${value}`}
                onClick={() => onLookbackChange(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!resultsByHorizon || !activeResult ? (
        <div className={styles.loadingPlaceholder}>
          <Spinner size="lg" block label="Loading valuation history…" />
        </div>
      ) : activeResult.isInsufficientData ? (
        // Same MIN_VALID_PAIRS check that disables this horizon's tab, but
        // checked here too: without this, the *active* horizon (e.g. the
        // hook's '5Y' default) could itself be insufficient on first load,
        // and this component would otherwise render a full, normal-looking
        // chart off too few points — the disabled tab alone doesn't stop
        // that, since "disabled" and "currently selected" aren't mutually
        // exclusive.
        <div className={styles.loadingPlaceholder}>
          {insufficientDataMessage(
            horizon,
            TIME_HORIZONS[horizon] / 12,
            lookbackYears,
            lookback
          )}
        </div>
      ) : (
        <div className={styles.chartWrap}>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          <span className={styles.rSquaredBadge}>
            R&sup2; = {(activeResult.rSquared * 100).toFixed(0)}%
          </span>
        </div>
      )}

      {correlationRows && <MetricCorrelationTable rows={correlationRows} valueBasis={valueBasis} />}
    </>
  );

  return embedded ? inner : <section className={styles.card}>{inner}</section>;
};

export default memo(ValuationSubsequentReturnsCard);
